import { Link, useLocation, useNavigate } from "react-router-dom";
import { NAV_ITEMS } from "@/lib/constants";
import { cn } from "@/lib/utils";
import { Logo } from "@/components/ui/logo";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { useSession } from "@/hooks/useSession";
import { getEnvironmentState, getEnvironmentLabel } from "@/lib/environment";
import { hasAnyRole, extractUserRoles, isAdminUser, ADMIN_ROLES, CONSUMER_ROLES, PARTNER_ROLES, ENTERPRISE_ROLES, type AppRole } from "@/lib/rbac";
import { AdminZoneSelector } from "@/components/admin/AdminZoneSelector";

interface SideNavProps {
  zone: "app" | "partner" | "admin" | "enterprise";
}

export function SideNav({ zone }: SideNavProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useSession();
  const zoneColor = zone === "app" ? "text-accent" : zone === "partner" ? "text-info" : zone === "enterprise" ? "text-primary" : "text-warning";
  const zoneLabel = zone === "app" ? "Application" : zone === "partner" ? "Partner Portal" : zone === "enterprise" ? "Enterprise" : "Admin Panel";
  const items = NAV_ITEMS[zone] ?? NAV_ITEMS["app"];

  const environment = getEnvironmentState(location.pathname, user);
  const environmentLabel = getEnvironmentLabel(environment);

  // Determine available zones based on user roles
  const userRoles = extractUserRoles(user);
  const userIsAdmin = isAdminUser(userRoles);
  const availableZones: Array<{zone: "app" | "partner" | "admin" | "enterprise", label: string, path: string}> = [];
  
  if (userIsAdmin) {
    // Admins can access all zones
    availableZones.push(
      { zone: "admin", label: "Admin Panel", path: "/admin" },
      { zone: "app", label: "User App", path: "/app" },
      { zone: "partner", label: "Partner Portal", path: "/partner" },
      { zone: "enterprise", label: "Enterprise", path: "/enterprise" },
    );
  } else {
    if (hasAnyRole(userRoles, CONSUMER_ROLES) || userRoles.length === 0) {
      availableZones.push({ zone: "app", label: "User App", path: "/app" });
    }
    if (hasAnyRole(userRoles, PARTNER_ROLES)) {
      availableZones.push({ zone: "partner", label: "Partner Portal", path: "/partner" });
    }
    if (hasAnyRole(userRoles, ADMIN_ROLES)) {
      availableZones.push({ zone: "admin", label: "Admin Panel", path: "/admin" });
    }
    if (hasAnyRole(userRoles, ENTERPRISE_ROLES)) {
      availableZones.push({ zone: "enterprise", label: "Enterprise", path: "/enterprise" });
    }
  }
  
  const showZoneSwitcher = availableZones.length > 1;

  return (
    <aside
      className="flex w-64 flex-col border-r border-sidebar-border bg-sidebar"
    >
      {/* Header */}
      <div className="flex h-16 items-center justify-between border-b border-sidebar-border px-4">
        <div className="flex items-center gap-3 overflow-hidden">
          <Logo size="sm" showText={true} href="/" />
          {environment === 'demo' ? (
            <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/20 text-[10px] font-bold uppercase tracking-wider text-amber-500 animate-blink-slow">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
              {environmentLabel}
            </div>
          ) : (
            <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-[10px] font-bold uppercase tracking-wider text-emerald-500 animate-blink-slow">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
              {environmentLabel}
            </div>
          )}
        </div>
      </div>

      {/* Zone label or switcher */}
      <div className="border-b border-sidebar-border px-4 py-3">
        {userIsAdmin ? (
          <AdminZoneSelector compact />
        ) : showZoneSwitcher ? (
          <div className="space-y-2">
            <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Switch Zone
            </span>
            <div className="grid grid-cols-2 gap-1.5">
              {availableZones.map((z) => (
                <button
                  key={z.zone}
                  onClick={() => navigate(z.path)}
                  className={cn(
                    "px-2 py-1.5 text-xs font-medium rounded-md transition-colors",
                    zone === z.zone
                      ? "bg-sidebar-accent text-sidebar-accent-foreground"
                      : "text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-accent-foreground"
                  )}
                >
                  {z.label}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <span className={cn("text-xs font-medium uppercase tracking-wider", zoneColor)}>
            {zoneLabel}
          </span>
        )}
      </div>

      {/* Nav items */}
      <nav className="flex-1 space-y-1 p-2">
        {items.map((item) => {
          const isActive = location.pathname === item.href ||
            (item.href !== `/${zone}` && location.pathname.startsWith(item.href));

          return (
            <Link
              key={item.href}
              to={item.href}
              className={cn(
                "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                isActive
                  ? "bg-sidebar-accent text-sidebar-accent-foreground"
                  : "text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
              )}
            >
              <span>{item.label}</span>
            </Link>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="border-t border-sidebar-border p-2">
        <Link
          to={`/${zone}/settings`}
          className="flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-sidebar-foreground/80 transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
        >
          <span>Settings</span>
        </Link>
        <button
          onClick={signOut}
          className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-sidebar-foreground/80 transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
        >
          <span>Sign out</span>
        </button>
      </div>
    </aside>
  );
}
